<script setup lang="ts">
import { computed } from 'vue';
import { useAbility } from '@casl/vue';
import BaseLayout from '@/layouts/BaseLayout.vue';
import SettingsRail from '@/components/settings/SettingsRail.vue';
import type {
  SettingsRailGroup,
  SettingsRailItem,
} from '@/components/settings/SettingsRail.vue';

const { can } = useAbility();

const groups = computed<SettingsRailGroup[]>(() => {
  const personal: SettingsRailGroup = {
    heading: 'Personal',
    items: [{ name: 'Notifications', route: 'SettingsNotificationsView' }],
  };

  if (!can('manage', 'Account')) return [personal];

  const accountItems: SettingsRailItem[] = [
    { name: 'Trackers', route: 'SettingsTrackersView' },
  ];

  if (can('manage', 'ContainerTypes')) {
    accountItems.push({
      name: 'Container Types',
      route: 'SettingsContainerTypesView',
    });
  }

  accountItems.push(
    { name: 'Shipments', route: 'SettingsShipmentsView' },
    { name: 'Geofences', route: 'SettingsGeofencesView' },
    { name: 'Geofence Types', route: 'SettingsGeofenceTypesView' },
    { name: 'Users', route: 'ManageUsersView' },
  );

  if (can('manage', 'ApiTokens') || can('view', 'LegacyApiKey')) {
    accountItems.push({ name: 'API Access', route: 'ManageAPIAccessView' });
  }

  return [
    personal,
    { heading: 'Account', items: accountItems },
    {
      heading: 'Billing',
      items: [
        {
          name: 'Subscriptions',
          route: 'ManageSubscriptionsView',
          relatedRoutes: ['ManageSubscriptionView'],
        },
        {
          name: 'Order History',
          route: 'ManageOrdersView',
          relatedRoutes: ['ManageOrderView'],
        },
        { name: 'Deposits', route: 'ManageDepositsView' },
        { name: 'Addresses', route: 'ManageAddressesView' },
        { name: 'Payment Methods', route: 'ManagePaymentView' },
      ],
    },
  ];
});
</script>

<template>
  <BaseLayout>
    <div
      class="mx-auto mb-[80px] w-full sm:mb-[120px] sm:mt-[64px] lg:min-w-[1024px]"
    >
      <h1 class="mb-[3px] text-gpx_2xl font-semibold text-gpx_black-500">
        Settings
      </h1>
      <p class="mb-[24px] text-sm text-gpx_gray-200">
        Your preferences, and everything this account is set up with.
      </p>
      <div
        class="grid grid-cols-1 gap-[24px] md:grid-cols-[216px_minmax(0,1fr)] md:gap-[34px]"
      >
        <SettingsRail :groups="groups" />
        <div class="min-w-0"><router-view /></div>
      </div>
    </div>
  </BaseLayout>
</template>
